/**
 * Utilitaires pour l'authentification
 */

import { auth } from '../services/firebase/config';

// URL de la fonction Cloud Firebase
const FIREBASE_FUNCTION_URL = 'https://us-central1-diotranscript.cloudfunctions.net/exchangeAzureTokenHttp';

/**
 * Vérifie si la fonction Cloud Firebase est accessible
 * @returns {Promise<Object>} Résultat du test avec statut et message
 */
export const checkFirebaseFunction = async () => {
  try {
    
    // Tentative d'accès à la fonction (sans token valide)
    const response = await fetch(FIREBASE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        test: true
      })
    });

    // Même si la fonction renvoie une erreur, le fait qu'elle réponde est suffisant
    // Une erreur 400 (Bad Request) ou 401 (Unauthorized) signifie que la fonction est accessible
    // mais que les paramètres ou l'authentification sont incorrects
    const isAccessible = response.status !== 404 && response.status !== 503;
    
    const result = {
      isAccessible,
      status: response.status,
      statusText: response.statusText,
      message: isAccessible 
        ? `Fonction Cloud accessible (${response.status})` 
        : `Fonction Cloud non accessible (${response.status})`
    };
    
    return result;
  } catch (error) {

    return {
      isAccessible: false,
      status: 0,
      statusText: 'Network Error',
      message: `Erreur réseau: ${error.message}`
    };
  }
};

/**
 * Obtient l'ID utilisateur actuel quelle que soit la méthode d'authentification
 * (Firebase Auth, Azure AD, etc.)
 * @returns {string|null} L'ID utilisateur ou null si non authentifié
 */
export const getCurrentUserId = () => {
  // Vérifier d'abord Firebase Auth
  const currentUser = auth.currentUser;
  if (currentUser) {
    return currentUser.uid;
  }

  // Vérifier ensuite l'utilisateur Azure dans localStorage
  try {
    const azureUserJson = localStorage.getItem('azureUser');
    if (azureUserJson) {
      const azureUser = JSON.parse(azureUserJson);
      
      // Essayer de récupérer l'ID à partir des différentes propriétés possibles
      const userId = azureUser.uid || azureUser.id || azureUser.objectId;
      
      if (userId) {
        return userId;
      }

    }
  } catch (error) {

  }
  
  // Vérifier la clé azureTestUser
  try {
    const azureTestUserJson = localStorage.getItem('azureTestUser');
    if (azureTestUserJson) {
      const azureTestUser = JSON.parse(azureTestUserJson);
      
      return azureTestUser.uid || azureTestUser.id || 'test-user';
    }
  } catch (error) {

  }
  
  // Vérifier la clé azureId (utilisée dans certaines implémentations)
  const azureId = localStorage.getItem('azureId');
  if (azureId) {
    return azureId;
  }

  return null;
};

/**
 * Vérifier si l'utilisateur est authentifié (quelle que soit la méthode)
 * @returns {boolean} true si l'utilisateur est authentifié
 */
export const isAuthenticated = () => {
  return getCurrentUserId() !== null;
};

/**
 * Fix l'ID utilisateur Azure en ajoutant le préfixe 'azure-' s'il n'existe pas déjà
 * @param {string} userId - ID utilisateur à corriger
 * @returns {string} ID utilisateur corrigé
 */
export const fixAzureUserId = (userId) => {
  if (!userId) return userId;
  
  // Si l'ID commence déjà par 'azure-', le retourner tel quel
  if (userId.startsWith('azure-')) {
    return userId;
  }
  
  // Sinon, ajouter le préfixe 'azure-'
  return `azure-${userId}`;
};

/**
 * Nettoie le localStorage des données d'authentification
 */
export const clearAuthData = () => {
  localStorage.removeItem('azureTestUser');
}; 